// state/simula-resume-storage.ts — validated Simula recovery storage.
// Running resume remains Guidata-only; pending-finalize recovery covers every
// mode and survives ordinary drawer navigation until persistence is confirmed.

import type { SimulaFinalizeAnswer, SimulaMode } from '../api/simula';
import type { QuestionDto } from '../api/kb';

export const SIMULA_RESUME_KEY = 'asc_simula_resume_v1';
export const SIMULA_RESUME_VERSION = 2 as const;
export const SIMULA_DURATION_MS = 20 * 60 * 1000;

// Snapshots created before RW2 R1 used the obsolete 30-minute duration. Accept
// that one legacy deadline long enough to normalize it to the official window;
// pending-finalize recovery must not be lost merely because the app deployed.
const LEGACY_SIMULA_DURATION_MS = 30 * 60 * 1000;

export const SIMULA_QUESTION_COUNT = 30;
const SIMULA_MODES: readonly SimulaMode[] = ['cieca', 'guidata', 'difficile'];

export type SimulaAnswerValue = 'Vero' | 'Falso';

export interface SimulaAnswerRecord {
  answer: SimulaAnswerValue;
  responseTimeMs: number;
  answeredAt: number;
}

interface SimulaSnapshotBase {
  version: typeof SIMULA_RESUME_VERSION;
  simulationId: string;
  mode: SimulaMode;
  questions: QuestionDto[];
  startedAt: number;
  deadlineAt: number;
  currentIndex: number;
  answers: Array<[string, SimulaAnswerRecord]>;
  savedAt: number;
}

export interface SimulaRunningSnapshot extends SimulaSnapshotBase {
  phase: 'running';
  mode: 'guidata';
}

export interface SimulaPendingFinalizeSnapshot extends SimulaSnapshotBase {
  phase: 'pending_finalize';
  payload: SimulaFinalizeAnswer[];
}

export type SimulaResumeSnapshot = SimulaRunningSnapshot | SimulaPendingFinalizeSnapshot;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFinitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isFiniteNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function isMode(value: unknown): value is SimulaMode {
  return typeof value === 'string' && SIMULA_MODES.includes(value as SimulaMode);
}

function parseQuestions(value: unknown): QuestionDto[] | null {
  if (!Array.isArray(value) || value.length !== SIMULA_QUESTION_COUNT) return null;
  const ids = new Set<string>();
  const questions: QuestionDto[] = [];

  for (const raw of value) {
    if (!isRecord(raw)) return null;
    if (!isNonBlank(raw.id) || ids.has(raw.id)) return null;
    if (!isNonBlank(raw.argomento_id) || !isNonBlank(raw.text_it)) return null;
    if (typeof raw.is_true !== 'boolean') return null;
    if (raw.image_ref !== null && typeof raw.image_ref !== 'string') return null;
    if (raw.argomenti !== undefined) {
      if (!isRecord(raw.argomenti) || !isNonBlank(raw.argomenti.name_it)) return null;
    }
    ids.add(raw.id);
    questions.push(raw as unknown as QuestionDto);
  }
  return questions;
}

function parseAnswers(
  value: unknown,
  questionIds: ReadonlySet<string>,
): Array<[string, SimulaAnswerRecord]> | null {
  if (!Array.isArray(value) || value.length > questionIds.size) return null;
  const ids = new Set<string>();
  const answers: Array<[string, SimulaAnswerRecord]> = [];

  for (const tuple of value) {
    if (!Array.isArray(tuple) || tuple.length !== 2 || !isNonBlank(tuple[0])) return null;
    const [questionId, rawRecord] = tuple;
    if (!questionIds.has(questionId) || ids.has(questionId) || !isRecord(rawRecord)) return null;
    if (rawRecord.answer !== 'Vero' && rawRecord.answer !== 'Falso') return null;
    if (!isFiniteNonNegativeInteger(rawRecord.responseTimeMs) || !isFinitePositive(rawRecord.answeredAt)) return null;
    ids.add(questionId);
    answers.push([
      questionId,
      {
        answer: rawRecord.answer,
        responseTimeMs: rawRecord.responseTimeMs,
        answeredAt: rawRecord.answeredAt,
      },
    ]);
  }
  return answers;
}

function parsePayload(
  value: unknown,
  questions: readonly QuestionDto[],
  answerMap: ReadonlyMap<string, SimulaAnswerRecord>,
): SimulaFinalizeAnswer[] | null {
  if (
    !Array.isArray(value)
    || value.length !== SIMULA_QUESTION_COUNT
    || questions.length !== SIMULA_QUESTION_COUNT
    || answerMap.size !== SIMULA_QUESTION_COUNT
  ) return null;
  const payload: SimulaFinalizeAnswer[] = [];

  for (let index = 0; index < questions.length; index += 1) {
    const raw = value[index];
    const question = questions[index];
    if (!isRecord(raw) || raw.questionId !== question.id) return null;
    if (typeof raw.answer !== 'boolean' || typeof raw.correctAnswer !== 'boolean') return null;
    if (raw.correctAnswer !== question.is_true || raw.answered !== true) return null;
    if (!isFiniteNonNegativeInteger(raw.responseTimeMs)) return null;

    const displayAnswer = answerMap.get(question.id);
    if (!displayAnswer) return null;
    if ((displayAnswer.answer === 'Vero') !== raw.answer) return null;
    if (displayAnswer.responseTimeMs !== raw.responseTimeMs) return null;

    payload.push({
      questionId: raw.questionId,
      answer: raw.answer,
      correctAnswer: raw.correctAnswer,
      responseTimeMs: raw.responseTimeMs,
      answered: raw.answered,
    });
  }
  return payload;
}

function parseBase(value: Record<string, unknown>): Omit<SimulaSnapshotBase, 'version'> | null {
  if (!isNonBlank(value.simulationId) || !isMode(value.mode)) return null;
  const questions = parseQuestions(value.questions);
  if (!questions) return null;
  if (!isFinitePositive(value.startedAt) || !isFinitePositive(value.deadlineAt)) return null;
  const officialDeadlineAt = value.startedAt + SIMULA_DURATION_MS;
  const legacyDeadlineAt = value.startedAt + LEGACY_SIMULA_DURATION_MS;
  if (value.deadlineAt !== officialDeadlineAt && value.deadlineAt !== legacyDeadlineAt) return null;
  if (!isFinitePositive(value.savedAt)) return null;
  if (!Number.isInteger(value.currentIndex) || (value.currentIndex as number) < 0 || (value.currentIndex as number) >= questions.length) return null;
  const questionIds = new Set(questions.map((question) => question.id));
  const answers = parseAnswers(value.answers, questionIds);
  if (!answers) return null;

  return {
    simulationId: value.simulationId,
    mode: value.mode,
    questions,
    startedAt: value.startedAt,
    deadlineAt: officialDeadlineAt,
    currentIndex: value.currentIndex as number,
    answers,
    savedAt: value.savedAt,
  };
}

function parseV2Snapshot(value: unknown): SimulaResumeSnapshot | null {
  if (!isRecord(value) || value.version !== SIMULA_RESUME_VERSION) return null;
  const base = parseBase(value);
  if (!base) return null;

  if (value.phase === 'running') {
    if (base.mode !== 'guidata') return null;
    return { version: SIMULA_RESUME_VERSION, phase: 'running', ...base, mode: 'guidata' };
  }
  if (value.phase !== 'pending_finalize') return null;
  const payload = parsePayload(value.payload, base.questions, new Map(base.answers));
  if (!payload) return null;
  return { version: SIMULA_RESUME_VERSION, phase: 'pending_finalize', ...base, payload };
}

function upgradeLegacyV1(value: unknown): SimulaRunningSnapshot | null {
  if (!isRecord(value) || value.version !== 1 || value.mode !== 'guidata') return null;
  return parseV2Snapshot({
    ...value,
    version: SIMULA_RESUME_VERSION,
    phase: 'running',
    deadlineAt: typeof value.startedAt === 'number' ? value.startedAt + SIMULA_DURATION_MS : Number.NaN,
  }) as SimulaRunningSnapshot | null;
}

function parseSnapshot(value: unknown): SimulaResumeSnapshot | null {
  if (isRecord(value) && value.version === 1) return upgradeLegacyV1(value);
  return parseV2Snapshot(value);
}

export function saveSimulaResumeSnapshot(snapshot: SimulaResumeSnapshot): boolean {
  const validated = parseV2Snapshot(snapshot);
  if (!validated) return false;
  try {
    localStorage.setItem(SIMULA_RESUME_KEY, JSON.stringify(validated));
    return true;
  } catch {
    return false;
  }
}

export function loadSimulaResumeSnapshot(): SimulaResumeSnapshot | null {
  try {
    const raw = localStorage.getItem(SIMULA_RESUME_KEY);
    if (!raw) return null;
    const parsed = parseSnapshot(JSON.parse(raw) as unknown);
    if (!parsed) {
      clearSimulaResumeSnapshot();
      return null;
    }
    return parsed;
  } catch {
    clearSimulaResumeSnapshot();
    return null;
  }
}

export function clearSimulaResumeSnapshot(): void {
  try {
    localStorage.removeItem(SIMULA_RESUME_KEY);
  } catch {
    // quota / privacy mode — best-effort, not fatal.
  }
}

/** Clear only an in-progress Guidata resume. A pending finalize is durable
 * recovery data and must survive ordinary drawer navigation. */
export function clearSimulaRunningSnapshot(): void {
  const snapshot = loadSimulaResumeSnapshot();
  if (snapshot?.phase === 'running') clearSimulaResumeSnapshot();
}

// ---------------------------------------------------------------------------
// FP4: recently-seen exam questions (drives /api/simula/start excludeQuestionIds)
// ---------------------------------------------------------------------------
// We persist the question ids of the last few exams so a fresh Simula start can
// ask the backend to hold them back, reducing back-to-back repeats now that the
// server samples the FULL pool at random. Best-effort only — a miss just means
// no exclusion, never a broken start. We cap the list so the exclude payload
// stays small (the start schema caps excludeQuestionIds at 500).

export const SIMULA_SEEN_KEY = 'asc_simula_seen_v1';

/** Keep roughly the last two exams' worth of question ids (2 × 30). */
const SIMULA_SEEN_MAX = 60;

/** Read the recently-seen exam question ids (most-recent first). */
export function loadSimulaSeenQuestionIds(): string[] {
  try {
    const raw = localStorage.getItem(SIMULA_SEEN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string').slice(0, SIMULA_SEEN_MAX);
  } catch {
    return [];
  }
}

/**
 * Prepend a fresh batch of question ids (e.g. the exam just started/finalized),
 * dedupe, and cap at SIMULA_SEEN_MAX. Returns the stored list. Best-effort.
 */
export function recordSimulaSeenQuestionIds(ids: string[]): string[] {
  try {
    const prior = loadSimulaSeenQuestionIds();
    const merged: string[] = [];
    const seen = new Set<string>();
    for (const id of [...ids, ...prior]) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(id);
      if (merged.length >= SIMULA_SEEN_MAX) break;
    }
    localStorage.setItem(SIMULA_SEEN_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return [];
  }
}
